import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import AttachmentApiService, { GalleryFile, AttachmentUsage } from '../../services/AttachmentApiService';
import ImageLightbox from '../ImageViewer';
import { FeedbackContext } from '../../contexts/FeedbackContext';
import { useTenant } from '../../contexts/TenantContext';
import {
  X,
  Search,
  UploadCloud,
  File,
  FileText,
  Image as ImageIcon,
  Table as TableIcon,
  Check,
  FolderOpen,
  ChevronLeft,
  ChevronRight,
  Loader2,
  ZoomIn,
  Layers,
  Eye,
  Edit2,
  Info,
  Download,
} from 'lucide-react';
import { openSignedAttachment, getSignedAttachmentUrl } from '../../utils/openSignedAttachment';
import * as S from './style';

export type { GalleryFile };

export interface AttachmentGalleryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect?: (selectedFiles: GalleryFile[]) => void;
  onSelectFiles?: (selectedFiles: GalleryFile[]) => void;
  multiple?: boolean;
  multiSelect?: boolean;
  defaultFilter?: 'all' | 'image' | 'pdf' | 'document' | 'spreadsheet' | 'other';
  title?: string;
}

export const ensureFileExtension = (name: string, fallbackFileName?: string | null): string => {
  const trimmed = (name || '').trim();
  if (!trimmed) return fallbackFileName || 'anexo';

  const extractExt = (val?: string | null) => {
    if (!val) return '';
    const clean = val.split('?')[0].split('#')[0].trim();
    const lastDot = clean.lastIndexOf('.');
    if (lastDot > 0 && lastDot < clean.length - 1) {
      const ext = clean.slice(lastDot + 1).toLowerCase();
      if (/^[a-z0-9]{1,10}$/i.test(ext)) return ext;
    }
    return '';
  };

  const currentExt = extractExt(trimmed);
  if (currentExt) return trimmed;

  const fallbackExt = extractExt(fallbackFileName);
  if (fallbackExt) return `${trimmed}.${fallbackExt}`;

  return trimmed;
};

export const AttachmentGalleryModal: React.FC<AttachmentGalleryModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  onSelectFiles,
  multiple = false,
  multiSelect = false,
  defaultFilter = 'all',
  title = 'Galeria de Anexos',
}) => {
  const navigate = useNavigate();
  const { setFeedback } = useContext(FeedbackContext);
  const { isFashionTenant } = useTenant();
  const isMultiple = Boolean(multiple || multiSelect);
  const [activeTab, setActiveTab] = useState<'gallery' | 'upload'>('gallery');
  const [files, setFiles] = useState<GalleryFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedMap, setSelectedMap] = useState<Record<number, GalleryFile>>({});

  const [failedImages, setFailedImages] = useState<Record<number, boolean>>({});
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const [usageFile, setUsageFile] = useState<GalleryFile | null>(null);
  const [usageList, setUsageList] = useState<AttachmentUsage[]>([]);
  const [loadingUsage, setLoadingUsage] = useState(false);

  const [activeMenuFileId, setActiveMenuFileId] = useState<number | null>(null);
  const [editingFile, setEditingFile] = useState<GalleryFile | null>(null);
  const [editName, setEditName] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>(defaultFilter);
  const [sortBy, setSortBy] = useState<string>('recent');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [dragOver, setDragOver] = useState(false);
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const [customTitle, setCustomTitle] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const loadGallery = useCallback(async () => {
    if (!isOpen) return;
    try {
      setLoading(true);
      const params: any = {
        page,
        limit: 24,
        sortBy,
      };
      if (debouncedSearch.trim()) {
        params.search = debouncedSearch.trim();
      }
      if (typeFilter && typeFilter !== 'all') {
        params.type = typeFilter;
      }

      const res = await AttachmentApiService.getGallery(params);
      if (res) {
        const rawFiles = res.data || [];
        const seen = new Set<string>();
        const dedupedFiles: GalleryFile[] = [];
        for (const f of rawFiles) {
          const key = f.originalName && f.fileSize ? `${f.originalName}:${f.fileSize}` : (f.fileName || f.fileUrl || String(f.id));
          if (!seen.has(key)) {
            seen.add(key);
            dedupedFiles.push(f);
          }
        }
        setFiles(dedupedFiles);
        setTotalPages(res.totalPages || 1);
        setTotalItems(dedupedFiles.length === rawFiles.length ? (res.total || 0) : dedupedFiles.length);
      }
    } catch (error) {
      console.error('Erro ao carregar galeria:', error);
      setFeedback({ isOpen: true, message: 'Erro ao carregar arquivos da galeria', statusOk: false });
    } finally {
      setLoading(false);
    }
  }, [isOpen, page, debouncedSearch, typeFilter, sortBy]);

  useEffect(() => {
    loadGallery();
  }, [loadGallery]);

  useEffect(() => {
    if (isOpen) {
      setSelectedMap({});
      setFileToUpload(null);
      setCustomTitle('');
      setActiveTab('gallery');
      setActiveMenuFileId(null);
      setEditingFile(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleWindowClick = () => setActiveMenuFileId(null);
    window.addEventListener('click', handleWindowClick);
    return () => window.removeEventListener('click', handleWindowClick);
  }, []);

  if (!isOpen) return null;

  const toggleSelect = (file: GalleryFile) => {
    if (isMultiple) {
      setSelectedMap((prev) => {
        const next = { ...prev };
        if (next[file.id]) {
          delete next[file.id];
        } else {
          next[file.id] = file;
        }
        return next;
      });
    } else {
      setSelectedMap((prev) => {
        if (prev[file.id]) {
          return {};
        }
        return { [file.id]: file };
      });
    }
  };

  const handleConfirmSelection = () => {
    const selectedList = Object.values(selectedMap);
    if (selectedList.length === 0) {
      setFeedback({ isOpen: true, message: 'Selecione ao menos um anexo', statusOk: false });
      return;
    }
    if (onSelectFiles) {
      onSelectFiles(selectedList);
    } else if (onSelect) {
      onSelect(selectedList);
    }
    onClose();
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const dropped = e.dataTransfer.files[0];
      setFileToUpload(dropped);
      if (!customTitle) {
        setCustomTitle(dropped.name);
      }
    }
  };

  const handleNativeFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selected = e.target.files[0];
      setFileToUpload(selected);
      if (!customTitle) {
        setCustomTitle(selected.name);
      }
    }
  };

  const handleUploadSubmit = async () => {
    if (!fileToUpload) {
      setFeedback({ isOpen: true, message: 'Selecione um arquivo para upload', statusOk: false });
      return;
    }

    try {
      setUploading(true);
      const uploaded = await AttachmentApiService.upload(fileToUpload, customTitle);

      if (uploaded) {
        setFeedback({ isOpen: true, message: 'Arquivo enviado com sucesso!', statusOk: true });
        setFileToUpload(null);
        setCustomTitle('');
        setSelectedMap({ [uploaded.id]: uploaded });
        setActiveTab('gallery');
        loadGallery();
      }
    } catch (error) {
      console.error('Erro no upload de anexo:', error);
      setFeedback({ isOpen: true, message: 'Falha ao enviar arquivo', statusOk: false });
    } finally {
      setUploading(false);
    }
  };

  const formatSize = (bytes: number): string => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const getFileBadgeInfo = (mimeType?: string | null, fileName?: string | null) => {
    const rawName = (fileName || '').trim();
    const ext = rawName ? rawName.split('?')[0].split('#')[0].split('.').pop()?.toLowerCase() || '' : '';
    const safeMime = (mimeType || '').toLowerCase();
    const imageExtensions = ['jpg', 'jpeg', 'jfif', 'pjpeg', 'pjp', 'png', 'webp', 'gif', 'svg', 'bmp', 'avif', 'ico', 'tif', 'tiff'];
    if (
      safeMime.startsWith('image/') ||
      imageExtensions.includes(ext)
    ) {
      return { type: 'image', icon: <ImageIcon size={28} /> };
    }
    if (safeMime.includes('pdf') || ext === 'pdf') {
      return { type: 'pdf', icon: <FileText size={28} /> };
    }
    if (
      safeMime.includes('sheet') || safeMime.includes('excel') || safeMime.includes('csv') ||
      ['xlsx', 'xls', 'csv', 'ods'].includes(ext)
    ) {
      return { type: 'sheet', icon: <TableIcon size={28} /> };
    }
    if (
      safeMime.includes('word') || safeMime.includes('officedocument') ||
      ['doc', 'docx', 'odt', 'rtf', 'txt'].includes(ext)
    ) {
      return { type: 'doc', icon: <FileText size={28} /> };
    }
    return { type: 'other', icon: <File size={28} /> };
  };

  const handleOpenUsage = async (file: GalleryFile) => {
    setUsageFile(file);
    setLoadingUsage(true);
    setUsageList([]);
    try {
      const res = await AttachmentApiService.getUsage(file.id);
      const list = Array.isArray(res)
        ? res
        : Array.isArray((res as any)?.usages)
        ? (res as any).usages
        : Array.isArray((res as any)?.data?.usages)
        ? (res as any).data.usages
        : [];
      setUsageList(list);
    } catch (err) {
      console.error(err);
      setFeedback({ isOpen: true, message: 'Erro ao consultar locais de uso do anexo', statusOk: false });
    } finally {
      setLoadingUsage(false);
    }
  };

  const getModuleBadgeLabel = (item: AttachmentUsage): string => {
    if (item.module === 'STOCK') {
      return isFashionTenant ? 'Tecidos' : 'Estoque';
    }
    if (item.module === 'RESOURCE') {
      return isFashionTenant ? 'Aviamentos' : 'Insumos';
    }
    return item.moduleLabel || item.module;
  };

  const getUsageEditUrl = (item: AttachmentUsage): string => {
    let baseUrl = '';
    if (item.editUrl) {
      if (!isFashionTenant && item.editUrl.includes('/panel/composition-product/edit/')) {
        baseUrl = item.editUrl.replace('/panel/composition-product/edit/', '/panel/product-final/edit/');
      } else if (item.editUrl.includes('/panel/service/edit/')) {
        baseUrl = item.editUrl.replace('/panel/service/edit/?id=', '/panel/production/edit/').replace('/panel/service/edit/', '/panel/production/edit/');
      } else {
        baseUrl = item.editUrl;
      }
    } else if (item.module === 'STOCK') {
      baseUrl = `/panel/stock?stockId=${item.entityId}&step=3`;
    } else if (item.module === 'RESOURCE') {
      baseUrl = `/panel/stock/resource/edit?id=${item.entityId}&step=3`;
    } else if (item.module === 'CERTIFICATE') {
      baseUrl = `/panel/certificates/edit?id=${item.entityId}`;
    } else if (item.module === 'OCCURRENCE') {
      baseUrl = `/panel/certificates/rnc?occurrenceId=${item.entityId}`;
    } else if (item.module === 'PRODUCT') {
      baseUrl = isFashionTenant
        ? `/panel/composition-product/edit/${item.entityId}`
        : `/panel/product-final/edit/${item.entityId}`;
    } else if (item.module === 'SERVICE_STAGE') {
      baseUrl = `/panel/production/edit/${item.entityId}`;
    } else if (item.module === 'TICKET') {
      baseUrl = `/panel/pipeline/ticket?ticketId=${item.entityId}`;
    } else if (item.module === 'ORDER') {
      baseUrl = `/panel/steel/edit/${item.entityId}`;
    } else if (item.module === 'USER') {
      baseUrl = `/panel/people?userId=${item.entityId}`;
    } else if (item.module === 'PRODUCT_APPROVAL' || item.module === 'CHECKLIST_ITEM' || item.module === 'BENEFICIAMENTO_NOTE') {
      baseUrl = `/panel/composition-product`;
    }

    if (item.module === 'USER' || baseUrl.startsWith('/panel/people')) {
      if (!baseUrl.includes('userId=')) {
        const separator = baseUrl.includes('?') ? '&' : '?';
        baseUrl = `${baseUrl}${separator}userId=${item.entityId}`;
      }
    }

    if (!baseUrl) return '';

    if (usageFile?.id) {
      const separator = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${separator}from=attachments&attachmentId=${usageFile.id}&returnTo=${encodeURIComponent(`/panel/attachments?attachmentId=${usageFile.id}`)}`;
    }

    return baseUrl;
  };

  const handleNavigateToRecord = (e: React.MouseEvent, url: string) => {
    if (!url) return;
    if (e.ctrlKey || e.metaKey || e.button === 1) {
      return;
    }
    e.preventDefault();
    navigate(url);
    setUsageFile(null);
    onClose();
  };

  const handleViewFile = (file: GalleryFile) => {
    setActiveMenuFileId(null);
    const badgeInfo = getFileBadgeInfo(file.mimeType, file.fileName || file.originalName || file.title || file.fileUrl);
    if (badgeInfo.type === 'image' && !failedImages[file.id]) {
      handleOpenLightbox(file.id);
    } else {
      openSignedAttachment(file.signedUrl || file.fileUrl, file.title || file.originalName);
    }
  };

  const handleDownloadFile = async (file: GalleryFile) => {
    setActiveMenuFileId(null);
    try {
      const safeDownloadName = ensureFileExtension(
        file.title || file.originalName || file.fileName || 'anexo',
        file.fileName || file.originalName,
      );

      let downloadUrl = '';
      try {
        downloadUrl = await AttachmentApiService.getDownloadUrl(file.id);
      } catch (err) {
        void err;
      }
      if (!downloadUrl) {
        downloadUrl = await getSignedAttachmentUrl(
          file.signedUrl || file.fileUrl,
          true,
          safeDownloadName,
        );
      }
      if (!downloadUrl) {
        setFeedback({ isOpen: true, message: 'Não foi possível obter o link de download', statusOk: false });
        return;
      }

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.download = safeDownloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Erro ao baixar anexo:', err);
      setFeedback({ isOpen: true, message: 'Erro ao realizar download do anexo', statusOk: false });
    }
  };

  const handleOpenEdit = (file: GalleryFile) => {
    setActiveMenuFileId(null);
    setEditingFile(file);
    setEditName(file.title || file.originalName);
  };

  const handleSaveEdit = async () => {
    if (!editingFile || !editName.trim()) {
      setFeedback({ isOpen: true, message: 'O nome do anexo não pode ser vazio', statusOk: false });
      return;
    }
    try {
      setSavingEdit(true);
      const targetOriginalName = ensureFileExtension(
        editName.trim(),
        editingFile.fileName || editingFile.originalName,
      );
      const updated = await AttachmentApiService.update(editingFile.id, {
        title: editName.trim(),
        originalName: targetOriginalName,
      });
      setFiles((prev) =>
        prev.map((f) =>
          f.id === editingFile.id
            ? { ...f, title: updated.title, originalName: updated.originalName }
            : f
        )
      );
      setSelectedMap((prev) => {
        if (!prev[editingFile.id]) return prev;
        return {
          ...prev,
          [editingFile.id]: {
            ...prev[editingFile.id],
            title: updated.title,
            originalName: updated.originalName,
          },
        };
      });
      setFeedback({ isOpen: true, message: 'Nome do anexo atualizado com sucesso!', statusOk: true });
      setEditingFile(null);
    } catch (error) {
      console.error(error);
      setFeedback({ isOpen: true, message: 'Erro ao atualizar nome do anexo', statusOk: false });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDetails = (file: GalleryFile) => {
    setActiveMenuFileId(null);
    handleOpenUsage(file);
  };

  const imageFiles = files.filter((f) => {
    const badgeInfo = getFileBadgeInfo(f.mimeType, f.fileName || f.originalName || f.title || f.fileUrl);
    return badgeInfo.type === 'image' && !failedImages[f.id];
  });

  const lightboxSlides = imageFiles.map((f) => ({
    src: f.signedUrl || f.fileUrl,
    title: f.title || f.originalName,
  }));

  const handleOpenLightbox = (fileId: number) => {
    const idx = imageFiles.findIndex((f) => f.id === fileId);
    if (idx !== -1) {
      setLightboxIndex(idx);
      setLightboxOpen(true);
    }
  };

  const selectedCount = Object.keys(selectedMap).length;

  return (
    <>
      {ReactDOM.createPortal(
        <S.ModalOverlay onClick={onClose}>
          <S.ModalContainer onClick={(e) => e.stopPropagation()}>
        <S.ModalHeader>
          <S.HeaderTitleArea>
            <FolderOpen size={20} color="#2563eb" />
            <S.ModalTitle>{title}</S.ModalTitle>
            <S.ItemCounterBadge>{totalItems} arquivos</S.ItemCounterBadge>
          </S.HeaderTitleArea>
          <S.CloseButton onClick={onClose} aria-label="Fechar">
            <X size={18} />
          </S.CloseButton>
        </S.ModalHeader>

        <S.TabsNav>
          <S.TabButton
            $active={activeTab === 'gallery'}
            onClick={() => setActiveTab('gallery')}
          >
            <FolderOpen size={16} />
            Galeria da Empresa
          </S.TabButton>
          <S.TabButton
            $active={activeTab === 'upload'}
            onClick={() => setActiveTab('upload')}
          >
            <UploadCloud size={16} />
            Novo Upload
          </S.TabButton>
        </S.TabsNav>

        <S.ModalBody>
          {activeTab === 'gallery' ? (
            <>
              <S.Toolbar>
                <S.SearchInputContainer>
                  <Search size={16} />
                  <S.SearchInput
                    type="text"
                    placeholder="Buscar anexo por nome..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </S.SearchInputContainer>

                <S.FilterChips>
                  <S.ChipButton
                    $active={typeFilter === 'all'}
                    onClick={() => {
                      setTypeFilter('all');
                      setPage(1);
                    }}
                  >
                    Todos
                  </S.ChipButton>
                  <S.ChipButton
                    $active={typeFilter === 'image'}
                    onClick={() => {
                      setTypeFilter('image');
                      setPage(1);
                    }}
                  >
                    <ImageIcon size={14} />
                    Imagens
                  </S.ChipButton>
                  <S.ChipButton
                    $active={typeFilter === 'pdf'}
                    onClick={() => {
                      setTypeFilter('pdf');
                      setPage(1);
                    }}
                  >
                    <FileText size={14} />
                    PDFs
                  </S.ChipButton>
                  <S.ChipButton
                    $active={typeFilter === 'document'}
                    onClick={() => {
                      setTypeFilter('document');
                      setPage(1);
                    }}
                  >
                    <File size={14} />
                    Docs
                  </S.ChipButton>
                  <S.ChipButton
                    $active={typeFilter === 'spreadsheet'}
                    onClick={() => {
                      setTypeFilter('spreadsheet');
                      setPage(1);
                    }}
                  >
                    <TableIcon size={14} />
                    Planilhas
                  </S.ChipButton>
                  <S.ChipButton
                    $active={typeFilter === 'other'}
                    onClick={() => {
                      setTypeFilter('other');
                      setPage(1);
                    }}
                  >
                    <FolderOpen size={14} />
                    Outros
                  </S.ChipButton>
                </S.FilterChips>

                <S.SortSelect
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="recent">Mais recentes</option>
                  <option value="oldest">Mais antigos</option>
                  <option value="name_asc">Nome (A-Z)</option>
                  <option value="name_desc">Nome (Z-A)</option>
                  <option value="size_desc">Maior tamanho</option>
                  <option value="size_asc">Menor tamanho</option>
                </S.SortSelect>
              </S.Toolbar>

              <S.GalleryContent>
                {loading ? (
                  <S.EmptyState>
                    <Loader2 size={32} className="animate-spin" />
                    <h3>Carregando arquivos...</h3>
                  </S.EmptyState>
                ) : files.length === 0 ? (
                  <S.EmptyState>
                    <FolderOpen size={48} />
                    <h3>Nenhum anexo encontrado</h3>
                    <p>
                      {debouncedSearch
                        ? 'Nenhum resultado para a busca. Tente outros termos.'
                        : 'Esta empresa ainda não possui arquivos nesta categoria.'}
                    </p>
                  </S.EmptyState>
                ) : (
                  <S.FilesGrid>
                    {files.map((file) => {
                      const isSelected = !!selectedMap[file.id];
                      const badgeInfo = getFileBadgeInfo(file.mimeType, file.fileName || file.originalName || file.title || file.fileUrl);
                      const isImg = badgeInfo.type === 'image';
                      const previewUrl = file.signedUrl || file.fileUrl;
                      const hasImageError = Boolean(failedImages[file.id]);

                      return (
                        <S.FileCard
                          key={file.id}
                          $selected={isSelected}
                          onClick={() => toggleSelect(file)}
                        >
                          <S.CardMenuButton
                            type="button"
                            title="Opções do anexo"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveMenuFileId((prev) => (prev === file.id ? null : file.id));
                            }}
                          >
                            ⋯
                          </S.CardMenuButton>

                          {activeMenuFileId === file.id && (
                            <S.CardMenuDropdown onClick={(e) => e.stopPropagation()}>
                              <S.CardMenuItem
                                type="button"
                                onClick={() => handleViewFile(file)}
                              >
                                <Eye size={14} />
                                Visualizar
                              </S.CardMenuItem>
                              <S.CardMenuItem
                                type="button"
                                onClick={() => handleDownloadFile(file)}
                              >
                                <Download size={14} />
                                Download
                              </S.CardMenuItem>
                              <S.CardMenuItem
                                type="button"
                                onClick={() => handleOpenEdit(file)}
                              >
                                <Edit2 size={14} />
                                Editar
                              </S.CardMenuItem>
                              <S.CardMenuItem
                                type="button"
                                onClick={() => handleDetails(file)}
                              >
                                <Info size={14} />
                                Ver detalhes
                              </S.CardMenuItem>
                            </S.CardMenuDropdown>
                          )}

                          <S.SelectionBadge $selected={isSelected}>
                            {isSelected && <Check size={14} strokeWidth={3} />}
                          </S.SelectionBadge>

                          <S.ThumbnailWrapper
                            onClick={(e) => {
                              if (isImg && !hasImageError) {
                                e.stopPropagation();
                                handleOpenLightbox(file.id);
                              }
                            }}
                            style={{ cursor: isImg && !hasImageError ? 'zoom-in' : 'pointer' }}
                          >
                            {isImg && !hasImageError ? (
                              <>
                                <img
                                  src={previewUrl}
                                  alt={file.title || file.originalName}
                                  loading="lazy"
                                  onError={() => {
                                    setFailedImages((prev) => ({ ...prev, [file.id]: true }));
                                  }}
                                />
                                <S.ZoomBadge>
                                  <ZoomIn size={12} />
                                </S.ZoomBadge>
                              </>
                            ) : (
                              <S.FileIconPlaceholder $type={badgeInfo.type}>
                                {badgeInfo.icon}
                                <span>
                                  {((file.fileName || file.originalName || file.title || file.fileUrl || '')
                                    .split('?')[0]
                                    .split('#')[0]
                                    .split('.')
                                    .pop() || 'ARQ'
                                  ).slice(0, 4).toUpperCase()}
                                </span>
                              </S.FileIconPlaceholder>
                            )}
                          </S.ThumbnailWrapper>

                          <S.FileInfo>
                            <S.FileName title={file.title || file.originalName}>
                              {file.title || file.originalName}
                            </S.FileName>
                            <S.FileMeta>
                              <span>{formatSize(file.fileSize)}</span>
                            </S.FileMeta>
                          </S.FileInfo>
                        </S.FileCard>
                      );
                    })}
                  </S.FilesGrid>
                )}
              </S.GalleryContent>
            </>
          ) : (
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <S.UploadDropzone
                $isDragOver={dragOver}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleFileDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <S.UploadIconCircle>
                  <UploadCloud size={28} />
                </S.UploadIconCircle>
                <div>
                  <S.UploadTitle>
                    {fileToUpload ? fileToUpload.name : 'Arraste e solte o arquivo aqui'}
                  </S.UploadTitle>
                  <S.UploadSubtitle>
                    {fileToUpload
                      ? `${formatSize(fileToUpload.size)} - Clique para trocar de arquivo`
                      : 'ou clique para navegar no seu computador'}
                  </S.UploadSubtitle>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: 'none' }}
                  onChange={handleNativeFileChange}
                />
              </S.UploadDropzone>

              <S.UploadFormArea>
                <S.InputGroup>
                  <S.InputLabel>Título / Descrição do arquivo (opcional)</S.InputLabel>
                  <S.CustomTitleInput
                    type="text"
                    placeholder="Ex: Ficha técnica do produto, Contrato assinado..."
                    value={customTitle}
                    onChange={(e) => setCustomTitle(e.target.value)}
                  />
                </S.InputGroup>

                <S.ConfirmButton
                  disabled={!fileToUpload || uploading}
                  onClick={handleUploadSubmit}
                  style={{ width: '100%', height: '42px' }}
                >
                  {uploading ? 'Enviando arquivo...' : 'Fazer Upload e Selecionar'}
                </S.ConfirmButton>
              </S.UploadFormArea>
            </div>
          )}
        </S.ModalBody>

        <S.ModalFooter>
          {activeTab === 'gallery' ? (
            <S.PaginationArea>
              <S.PageButton
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft size={14} />
              </S.PageButton>
              <span>
                Página {page} de {totalPages || 1}
              </span>
              <S.PageButton
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                <ChevronRight size={14} />
              </S.PageButton>
            </S.PaginationArea>
          ) : (
            <div />
          )}

          <S.ActionsArea>
            <S.CancelButton onClick={onClose}>Cancelar</S.CancelButton>
            <S.ConfirmButton
              disabled={selectedCount === 0}
              onClick={handleConfirmSelection}
            >
              {selectedCount > 0
                ? `Usar anexo selecionado (${selectedCount})`
                : 'Selecione um anexo'}
            </S.ConfirmButton>
          </S.ActionsArea>
        </S.ModalFooter>
        {usageFile && (
          <S.UsageModalOverlay onClick={() => setUsageFile(null)}>
            <S.UsageModalContainer onClick={(e) => e.stopPropagation()}>
              <S.UsageModalHeader>
                <S.UsageModalTitle>
                  <Layers size={18} color="#2563eb" />
                  Locais de Uso do Anexo
                </S.UsageModalTitle>
                <S.CloseButton onClick={() => setUsageFile(null)} aria-label="Fechar">
                  <X size={18} />
                </S.CloseButton>
              </S.UsageModalHeader>

              <S.UsageModalBody>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 8, borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>
                    {usageFile.title || usageFile.originalName}
                  </span>
                </div>

                {loadingUsage ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '30px 0', gap: 8, color: '#64748b' }}>
                    <Loader2 size={20} className="animate-spin" />
                    <span style={{ fontSize: '0.85rem' }}>Consultando vínculos...</span>
                  </div>
                ) : usageList.length === 0 ? (
                  <S.UsageEmpty>
                    <FolderOpen size={32} color="#cbd5e1" />
                    <span style={{ fontWeight: 600, color: '#475569' }}>Nenhum uso encontrado</span>
                    <span style={{ fontSize: '0.78rem' }}>Este anexo não está vinculado a nenhum card, pedido ou produto no momento.</span>
                  </S.UsageEmpty>
                ) : (
                  usageList.map((item, idx) => {
                    const editUrl = getUsageEditUrl(item);
                    return (
                      <S.UsageItem key={`${item.module}-${item.entityId}-${idx}`}>
                        <S.UsageItemContent>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <S.UsageModuleBadge>{getModuleBadgeLabel(item)}</S.UsageModuleBadge>
                            {item.isCover && <S.UsageCoverBadge>Capa</S.UsageCoverBadge>}
                          </div>
                          {editUrl ? (
                            <S.UsageRecordLink
                              href={editUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => handleNavigateToRecord(e, editUrl)}
                              title={`Abrir e editar ${item.recordName}`}
                            >
                              {item.recordName}
                            </S.UsageRecordLink>
                          ) : (
                            <S.UsageRecordName title={item.recordName}>{item.recordName}</S.UsageRecordName>
                          )}
                        </S.UsageItemContent>
                        {editUrl && (
                          <S.UsageItemActions>
                            <S.UsageLinkButton
                              href={editUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => handleNavigateToRecord(e, editUrl)}
                              title="Abrir para editar este registro"
                            >
                              <Edit2 size={13} />
                              <span>Editar</span>
                            </S.UsageLinkButton>
                          </S.UsageItemActions>
                        )}
                      </S.UsageItem>
                    );
                  })
                )}
              </S.UsageModalBody>
            </S.UsageModalContainer>
          </S.UsageModalOverlay>
        )}

        {editingFile && (
          <S.EditModalOverlay onClick={() => setEditingFile(null)}>
            <S.EditModalContainer onClick={(e) => e.stopPropagation()}>
              <S.EditModalHeader>
                <S.EditModalTitle>
                  <Edit2 size={18} color="#2563eb" />
                  Editar Nome do Anexo
                </S.EditModalTitle>
                <S.CloseButton onClick={() => setEditingFile(null)} aria-label="Fechar">
                  <X size={18} />
                </S.CloseButton>
              </S.EditModalHeader>
              <S.EditModalBody>
                <S.EditModalLabel>Nome / Título do Arquivo</S.EditModalLabel>
                <S.EditModalInput
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveEdit();
                    if (e.key === 'Escape') setEditingFile(null);
                  }}
                />
              </S.EditModalBody>
              <S.EditModalFooter>
                <S.CancelButton type="button" onClick={() => setEditingFile(null)}>
                  Cancelar
                </S.CancelButton>
                <S.ConfirmButton
                  type="button"
                  disabled={savingEdit || !editName.trim()}
                  onClick={handleSaveEdit}
                >
                  {savingEdit ? 'Salvando...' : 'Salvar'}
                </S.ConfirmButton>
              </S.EditModalFooter>
            </S.EditModalContainer>
          </S.EditModalOverlay>
        )}
      </S.ModalContainer>
    </S.ModalOverlay>,
    document.body,
  )}
  <ImageLightbox
    open={lightboxOpen}
    close={() => setLightboxOpen(false)}
    slides={lightboxSlides}
    index={lightboxIndex}
  />
</>
  );
};

export default AttachmentGalleryModal;
